/**
 * Typed API client for the E2E Fax Scheduler backend.
 * Handles auth redirects on 401 and provides typed request methods.
 */

import type { InboundMethod, OutboundMethod } from '../utils/methods';
import { applyProviderLabelTables } from '../utils/methods';
// W1-F. Declared beside the phrasing helpers that consume it, the way the outbound-handle
// labels live in `utils/outboundHandle.ts` rather than in this file.
import type { LegApiEndpoint } from '../utils/apiEndpoint';

export type { LegApiEndpoint };

// Our own backend prefix. Nothing to do with the outbound method named 'eapi'.
const BASE_URL = '/api';

class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown) {
    super(`API Error ${status}`);
    this.status = status;
    this.body = body;
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const url = path.startsWith('/') ? path : `${BASE_URL}/${path}`;
  const response = await fetch(url, {
    headers: { 'Content-Type': 'application/json', ...options.headers as Record<string, string> },
    ...options,
  });

  if (response.status === 401) {
    window.location.href = '/auth/login';
    throw new ApiError(401, 'Not authenticated');
  }

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new ApiError(response.status, body);
  }

  if (response.status === 204) return undefined as T;
  return response.json();
}

/**
 * Turn any thrown API error into text a human can act on.
 *
 * FastAPI returns 422 validation errors as an ARRAY of objects
 * ([{loc, msg, type, ...}]), not a string. Passing that straight into JSX
 * ({error}) makes React throw "Objects are not valid as a React child", so the
 * user saw either a blank error box or a crashed page instead of the reason the
 * save was rejected. Flatten it here, once, rather than in every page.
 */
export function formatApiError(err: any, fallback = 'Request failed'): string {
  const detail = err?.body?.detail ?? err?.body
  if (typeof detail === 'string' && detail) return detail

  if (Array.isArray(detail)) {
    const parts = detail
      .map((d: any) => {
        if (typeof d === 'string') return d
        // loc is like ["body", "smtp_password"]; drop the leading "body" so the
        // message reads as a field name rather than framework plumbing.
        const loc = Array.isArray(d?.loc)
          ? d.loc.filter((x: unknown) => x !== 'body').join('.')
          : ''
        const msg = d?.msg || d?.message || ''
        // Pydantic prefixes model-level validator messages with "Value error, ".
        const clean = String(msg).replace(/^Value error,\s*/i, '')
        return loc && !clean.toLowerCase().includes(loc.toLowerCase())
          ? `${loc}: ${clean}`
          : clean
      })
      .filter(Boolean)
    if (parts.length) return parts.join(' · ')
  }

  if (detail && typeof detail === 'object') {
    const msg = (detail as any).msg || (detail as any).message
    if (msg) return String(msg)
  }

  return err?.message || fallback
}

// --- Job API ---

/**
 * One leg's provider and transport, WITH the resolution that produced them.
 *
 * `provider_source` is part of the answer, not metadata about it: 'row' means the measurement
 * row's own stamp, which is the only authoritative account of what carried THIS fax; 'job'
 * means the job's current configuration, true of the next send and only presumed of this one;
 * 'default' means nothing was recorded anywhere. A panel that flattens the three tells an
 * operator a measurement exists when it does not.
 */
export interface InfoLeg {
  leg: 'outbound' | 'inbound';
  provider: string;
  provider_label: string;
  provider_source: 'row' | 'job' | 'default';
  provider_recorded: boolean;
  /** Registry transport name ('api' | 'smtp' | 'webhook' | 'imap'), null when unknown. */
  transport: string | null;
  transport_source: 'row' | 'job_method' | 'unknown';
  /** The JOB's stored method value ('eapi' | 'smtp' | 'eapi_webhook' | 'imap'). */
  method: string | null;
  /** Display label for the pair, e.g. "eFax SMTP", "SFax API". */
  label: string;
}

/** One record the row holds, NAMING the column it came from. */
export interface InfoSource {
  /** The column name(s), verbatim — an operator cannot otherwise tell these tables apart. */
  source: string;
  leg: 'outbound' | 'inbound';
  label: string;
  /** False means THIS ROW has no such record — never "this build has no such panel". */
  present: boolean;
  /**
   * Whether this source CAN apply to these legs — the third state.
   *
   * `present` alone conflated "this row recorded nothing here" with "this source could never
   * apply to this transmission". An IMAP-inbound job has no callback payload and never will,
   * and an empty callback panel invites a reader to hunt for a delivery that was never going
   * to exist. `null` means the transport is unknown (a historical row whose leg columns were
   * never backfilled) — which is deliberately not the same as inapplicable, because claiming
   * a source cannot apply to a row we cannot classify would be inventing a fact about it.
   */
  applicable?: boolean | null;
  received_at?: string | null;
  message_id?: string | null;
  data?: Record<string, unknown>;
  /** True for a deliberately unimplemented view; `reason` then says what it waits for. */
  stub?: boolean;
  reason?: string;
}

/**
 * Response of GET /api/transmissions/{id}/outbound-info.
 *
 * Replaces GET /api/runs/{id}/smtp-info, which resolved runs only — so Info on a batch item
 * 404'd while the WRITER handled both tables. The old path redirects, and every key it used to
 * return is still here.
 */
export interface OutboundInfo {
  transmission_id: string;
  /** Which table answered. A panel that cannot say cannot link to the right detail page. */
  kind: 'run' | 'batch_item';
  /** Same value as transmission_id, kept so smtp-info-era code keeps working. */
  run_id: string;
  run_token?: string | null;
  job_id: string;
  outbound: InfoLeg;
  inbound: InfoLeg;
  /** The provider-agnostic outbound handle from `efax_fax_id`. */
  outbound_handle: string | null;
  /** The name ITS OWN provider gives that handle. Never hardcode "eFax fax_id". */
  outbound_handle_label: string;
  outbound_method: string | null;
  /** Loaded at last: it is what distinguishes "no notification yet" from "not ever". */
  inbound_method: string | null;
  smtp_message_id: string | null;
  notification_received: boolean;
  notification_received_at: string | null;
  x_headers: Record<string, string>;
  all_headers: Record<string, string>;
  sources: InfoSource[];
  /** Present only for an SFax outbound leg. `available: false` carries its own reason. */
  sfax_callback_record?: {
    available: boolean;
    key?: string;
    record?: Record<string, unknown>;
    scanned?: number;
    truncated?: boolean;
    reason?: string;
  } | null;
  t_submitted: string | null;
  t_fax_sent: string | null;
  /**
   * THE INBOUND LEG'S CLOCK — the reception time, which this response did NOT carry (W2-2A-1).
   *
   * That omission is why the received time looked like an eFax-eAPI and SRFax-API feature.
   * `POST /api/runs/{id}/check-status` has always returned `t_received`, and it is reachable
   * only for a provider/transport pair with a LIVE provider lookup; every other transmission
   * opens the local panel, which this interface describes — and it published the outbound
   * clock and stopped. So an SFax run and every SMTP-sent run showed `Submitted` and `Fax
   * Sent` with no reception time, on rows where `t_received` was populated and correct.
   */
  t_received?: string | null;
  /**
   * WHICH source produced `t_received` on THIS row — one of the two stored provenance codes.
   *
   * Carried with the value and not optional decoration: this column is one of only two whose
   * value comes from a runtime choice (the provider's notification timestamp, a mail header,
   * or our own arrival clock), so a reception time shown without it cannot be told apart from
   * a rung-1 reading.
   */
  t_received_source?: string | null;
  /**
   * OUR clock when we learned of the arrival, kept separate from `t_received` on purpose:
   * `t_notified - t_received` is our own pickup lag and nothing else.
   */
  t_notified?: string | null;
  /**
   * Whether the inbound leg has CLOSED. `t_received !== null` server-side, published rather
   * than left to the client: `status` answers a different question (a receive-timeout row is
   * terminal with no reception, a matched row can still be mid-scoring), so a client deriving
   * this from status would report one of those as the other.
   */
  inbound_leg_closed?: boolean;
  /**
   * Resolved provenance per timestamp column, from the backend registry — the SAME projection
   * run detail and batch item detail read, so this panel cannot describe one row's authority
   * differently from the pages beside it.
   */
  timestamp_provenance?: Record<string, {
    code?: string | null;
    rung?: 0 | 1 | 2 | 3 | null;
    event?: string | null;
    field?: string | null;
    transport?: string | null;
    caveat?: string | null;
    bound_by_poll_interval_seconds?: number | null;
  }> | null;
  outbound_status: string | null;
  status: string;
  /** The notes joined, for a panel that renders one block. */
  note: string | null;
  notes: string[];
}

export interface SmtpInfo {
  run_id: string;
  run_token?: string | null;
  /** The JOB's stored outbound method, not a literal. Used to LABEL the panel, and to
   *  notice when this view is showing a run that never went out over SMTP — in which
   *  case its empty fields mean "wrong view", not "data missing". */
  outbound_method: string | null;
  /** Remote server's queue ID from its acceptance reply. null when its reply used a
   *  format the parser did not recognise (the raw reply is in the logs). */
  smtp_message_id: string | null;
  /** Authoritative "did a notification arrive" flag. Do NOT infer this from an
   *  empty x_headers — that cannot distinguish "nothing arrived" from "arrived
   *  with no headers". */
  notification_received: boolean;
  notification_received_at: string | null;
  x_headers: Record<string, string>;
  all_headers: Record<string, string>;
  t_submitted: string | null;
  t_fax_sent: string | null;
  outbound_status: string | null;
  status: string;
  note: string | null;
}

/**
 * Response of POST /api/runs/{id}/check-status — the eAPI half of the Info button.
 *
 * Every field is optional except the ids and status because the endpoint returns ONE
 * shape for every outcome: a full transmission record, and the legitimate "not yet"
 * states (no fax id recorded, credentials removed) which differ only in carrying a
 * `note`. Two shapes would mean a blank field could not be told from a wrong panel.
 */
export interface RunStatusInfo {
  run_id: string;
  run_token?: string | null;
  outbound_method?: string | null;
  /** The RESOLVED outbound provider. check-status now refuses a non-eFax row rather than
   *  asking eFax about another platform's identifier, and says so in `note`. */
  outbound_provider?: string | null;
  outbound_provider_label?: string | null;
  /** False when the provider was inferred from the job or the default, not recorded on the
   *  row. An inference must not be displayed as a measurement. */
  outbound_provider_recorded?: boolean;
  /** What the resolved provider calls `efax_fax_id`. Never label that field yourself. */
  outbound_handle_label?: string | null;
  status: string;
  outbound_status?: string | null;
  t_submitted?: string | null;
  t_fax_sent?: string | null;
  t_received?: string | null;
  efax_fax_id?: string | null;
  metadata?: Record<string, unknown> | null;
  metadata_error?: string | null;
  /** Plain-language explanation when there is nothing (yet) to show. */
  note?: string | null;
}

// ---------------------------------------------------------------------------------------
// GET /api/providers — the registry as a descriptor the job editor RENDERS FROM.
//
// This replaces the frontend's own copies of the eFax host list and gateway domains, which
// were pinned to the backend only by a source-text test. A host the UI offers can no longer
// be a host the backend rejects, because both read the same dataclass.
//
// The descriptor deliberately carries no VALUES and no `column`: it describes the SHAPE of a
// provider's configuration. A job's own response carries its `has_*` presence booleans.
// ---------------------------------------------------------------------------------------

/** How a credential field is treated on the wire and on edit. */
export type CredentialSecrecy = 'plain' | 'secret' | 'clearable';

export interface ProviderCredentialField {
  key: string;
  label: string;
  kind: 'text' | 'password' | 'int' | 'bool' | 'select';
  /**
   * `plain` echoes back and a blank submission means blank. `secret` is masked and a blank
   * submission means KEEP. `clearable` is masked, a blank submission means CLEAR and an
   * omitted field means keep — which is the only way to remove a stored secret.
   *
   * Masking and required-ness are DERIVED from this plus `presence_flag`; neither is
   * hardcoded per provider anywhere in the form.
   */
  secrecy: CredentialSecrecy;
  /** Required WHEN NOT ALREADY STORED — see `presence_flag`. */
  required: boolean;
  /** The job's `has_*` boolean proving something is stored. Several fields may share one. */
  presence_flag?: string | null;
  help?: string | null;
  /**
   * Read from the JOB even when the leg names an account (4a), so the job window renders it
   * ALONGSIDE the account picker rather than being replaced by it. Only `efax_smtp_send_domain`
   * sets it today: which eFax gateway a fax is addressed to is a property of that fax, and while
   * the picker hid every field of the leg it had a column, a validator and no surface.
   *
   * NOT derivable client-side as "in `fields` but not on the account": that difference also
   * contains the username, password and From address, which the account DOES supply and which
   * must stay hidden.
   */
  job_owned?: boolean;
}

export interface ProviderTransportDescriptor {
  /** Registry transport name: 'api' | 'smtp' | 'webhook' | 'imap'. NOT the stored method. */
  transport: string;
  label: string;
  /** Rendered in DECLARED ORDER, with no per-provider JSX. */
  fields: ProviderCredentialField[];
  /** Present only on `api`. A closed set — the host is a <select>, never a text input. */
  hosts?: string[];
  /** Present only on `smtp`. */
  gateway_domains?: string[];
  /**
   * Where THIS LEG resolves its credentials: 'job' or 'account'. Per leg, because a
   * provider's legs disagree — the coarse provider-level `credential_scope` cannot describe
   * one brand whose API leg reads an account while its IMAP leg reads our own mailbox.
   */
  scope: string;
  /**
   * Which `provider_account.account_kind` owns this leg's credentials, and THE gate for
   * rendering an account picker on this leg. `null` means the leg has no account record at
   * all — eFax's inbound webhook, whose HMAC secret is genuinely per job — so that leg keeps
   * its own typed fields. NOT a provider slug: `imap_mailbox` is shared across brands.
   */
  account_kind?: string | null;
}

/**
 * A NULL capability means NOT APPLICABLE ON THIS PROVIDER, which the UI states as
 * unavailable rather than rendering a blank indistinguishable from a gap.
 */
export interface ProviderCapabilities {
  polls_outbound_status: boolean;
  has_provider_accept_timestamp: boolean;
  /**
   * Whether this provider's headers carry a parseable pod/region identity. eFax only.
   *
   * THIS IS A CAPABILITY, AND IT IS NOT THE BATCH CROSS-CHECK. `PodCrossCheck` below
   * answers a completely different question — "did THIS batch measure the pod it says it
   * measured" — and it is an object, not a boolean. The backend deliberately publishes it
   * under the key `pod_cross_check` for that reason: one name meaning a boolean on
   * `/api/providers` and an object on `/api/batches/{id}` would not be caught by the
   * compiler, because the two live on different response types. See B2.16.
   */
  pod_attribution: boolean;
  webhook_method?: string | null;
  webhook_scope?: string | null;
  inbound_download_credentials?: string | null;
  inbound_email_documented: boolean;
}

/**
 * One item whose observed pod disagreed with the batch's target.
 *
 * Identified by `run_token`, which is what every other surface and every log line in this
 * system calls a transmission — so a finding here can be followed up without a join.
 * `observed_msg_id` is eFax's raw `J2MsgID`, carried so a mismatch can be checked against
 * the provider's own bytes instead of believed.
 */
export interface PodCrossCheckItem {
  run_token: string | null;
  batch_seq: number | null;
  observed_pod: string | null;
  observed_environment: string | null;
  observed_msg_id: string | null;
  pod_outcome: string;
  environment_outcome: string;
}

/**
 * DID THIS BATCH MEASURE THE POD IT SAYS IT MEASURED? (B2.6, surfaced by B2.16)
 *
 * The pod the operator deliberately relayed to, against the pod eFax's own `J2MsgID` says
 * handled each fax. THIS OBJECT is present only on `GET /api/batches/{id}` — `mismatched_items`
 * and the per-item counts need the items, which only that endpoint loads.
 *
 * THE OUTCOME ALONE IS ALSO ON THE SUMMARY PAYLOADS, as `BatchResponse.pod_cross_check_outcome`
 * (B2.27). Until then a mismatch rendered only on a batch's own report page, so a batch
 * launched yesterday and skimmed in a list showed nothing and a mis-attributed batch was found
 * only by somebody already looking at it. The listing computes that outcome from ONE aggregate
 * query for the page, never one per row, and `tests/test_internal_batch_list_signals.py` asserts
 * the summary equals this object's `outcome` for the same batch — the indicator and the detail
 * cannot drift. The listing still carries no part of the detail below.
 *
 * A DIAGNOSTIC, NEVER A VERDICT. A fax that went out and came back passed, whichever pod
 * carried it, so nothing here may be rendered as a pass/fail or allowed to colour one. What
 * a disagreement devalues is the ATTRIBUTION of the measurement, not the measurement.
 *
 * `outcome` is one of `'agreement' | 'mismatch' | 'not_recorded'`, typed as `string` rather
 * than a union on purpose: the vocabulary is declared in `backend/services/agent_dispatch.py`
 * and a hand-copied union here would be a second declaration that silently stops matching.
 * `PodCrossCheck.tsx` renders an unrecognised value visibly unrecognised instead.
 *
 * THREE STATES, AND ON THE SUMMARY A FOURTH THING THAT IS NOT A STATE: `not_recorded` means a
 * comparison was attempted and nothing was there to compare, while a NULL
 * `pod_cross_check_outcome` means the question does not arise at all — a public batch aimed at
 * no pod. Collapsing either into `agreement` would render an unchecked batch as a confirmed
 * one, which is the flattering error this surface exists to avoid.
 *
 * NOT CALLED `pod_attribution`, which is already a provider capability BOOLEAN — see
 * `ProviderCapabilities.pod_attribution` above.
 */
export interface PodCrossCheck {
  outcome: string;
  send_path: string | null;
  target_pod: string | null;
  target_environment: string | null;
  /** Every distinct pod eFax reported across the batch. More than one entry means the batch's
   * items were not all handled by the same pod, whatever the target said. */
  observed_pods: string[];
  items: number;
  agreement: number;
  mismatch: number;
  not_recorded: number;
  mismatched_items: PodCrossCheckItem[];
  /** Always false today. The two sides are in different vocabularies — our agent's short code
   * (`use1`) against the batch's AWS region (`us-east-1`) — so comparing them as strings would
   * report a mismatch on every correctly-targeted fax. */
  region_compared: boolean;
  region_not_compared_because: string;
}

/**
 * An SFax account record as the job editor needs it. Secrets are never returned — only
 * `has_*` proof that a value is stored — so a half-configured account is visible as such
 * rather than looking complete and failing at send time.
 */
export interface SfaxAccountSummary {
  id: string;
  display_name: string;
  enabled: boolean;
  has_username: boolean;
  has_api_key: boolean;
  has_encryption_key: boolean;
  has_init_vector: boolean;
}

/**
 * A DID an account declares it owns. `did_normalised` is what matching compares; `did` is what
 * the operator typed, kept because that is what they will recognise in a select.
 */
export interface DeclaredDid {
  id: string;
  did: string;
  did_normalised: string;
  /**
   * The `+`-prefixed E.164 form to DISPLAY. Served by the API rather than derived here so the
   * `+1`-for-bare-ten-digits assumption lives in one place with its reasoning; rendering `did`
   * raw in one screen and canonical in another is what made one number look like two.
   */
  did_display: string;
  role: string;
}

/**
 * A DID an account's own MAILBOX ADDRESS implies — derived by the server on read, never stored.
 *
 * SEPARATE FROM `DeclaredDid` ON PURPOSE, and the missing `id` is the reason. A declared DID is
 * a row: it has an id to delete, the inbound-uniqueness index constrains it, and the admission
 * filter answers "is this delivery mine" from it. A derived DID is an observation about the
 * mailbox, recomputed every read — so merging the two would leave a surface unable to tell a
 * number the operator declared from one we inferred.
 *
 * `declared` true means a stored row in this role already names the same number (compared on the
 * last ten digits), so the derivation only corroborates it and the stored row should be used.
 */
export interface DerivedDid {
  did: string;
  did_normalised: string;
  /** The `+`-prefixed form to display, served for the same reason `DeclaredDid.did_display` is. */
  did_display: string;
  role: string;
  /** `imap` for the inbound role, `smtp` for the ANI. */
  transport: string;
  /** The credential field the DID was read out of, so a surface can say where it came from. */
  source_field: string;
  /** Always `address` — a derived value's provenance in the backend's own vocabulary. */
  did_source: string;
  declared: boolean;
}

/**
 * A provider account of ANY kind, as the job editor needs it — the general surface over the
 * same table `SfaxAccountSummary` sees through the pinned-kind alias. Carries `dids`, which the
 * alias response does not, because the destination-DID select is chosen from them (T5.41).
 */
export interface ProviderAccountSummary {
  id: string;
  account_kind: string;
  display_name: string;
  enabled: boolean;
  dids: DeclaredDid[];
  /**
   * DIDs the account's mailbox address implies (U28 stage 3). Optional because every OTHER
   * consumer of this type predates the field and a response from an older backend legitimately
   * omits it — a required field would make the whole job editor fail to parse rather than simply
   * not pre-fill.
   */
  derived_dids?: DerivedDid[];
}

export interface ProviderDescriptor {
  slug: string;
  label: string;
  /** 'job' when credentials live on the job; 'account' for a shared account record. */
  credential_scope: string;
  outbound: ProviderTransportDescriptor[];
  inbound: ProviderTransportDescriptor[];
  capabilities: ProviderCapabilities;
  /**
   * DISPLAY LABELS for this provider and its transports (G4.2 / T4.20).
   *
   * Provider-specific by necessity: eFax's REST API is branded 'eAPI' and SFax's plainly
   * 'API', so one shared transport table would print 'SFax eAPI'. `methods.ts` merges this
   * over its own literals and keeps them as a build-time fallback, so an older backend that
   * omits the block leaves labels working rather than blank.
   */
  labels?: {
    provider: string;
    transports: Record<string, string>;
    not_recorded: string;
    transport_not_recorded_suffix: string;
  } | null;
  constants: {
    smtp_gateway_body_pages?: number | null;
    header_band_inches?: number | null;
    poll_interval_seconds?: number | null;
    document_types: string[];
  };
}

/**
 * The quality score distribution for one job (T10.31).
 *
 * Every optional number here is optional for a REASON: it is absent when the sample cannot
 * support it. `suppressed` carries one sentence per absent figure, and the editor renders
 * those sentences instead of drawing a chart from four points.
 */
export interface QualityDistributionBin {
  lower: number;
  upper: number;
  count: number;
}

export interface QualityDistribution {
  job_id: string;
  job_name: string;
  quality_check_enabled: boolean;
  min_quality_score?: number | null;
  /** The scoring generation these figures are on. Scores from two generations are not on one scale. */
  quality_score_version?: number | null;
  versions_present: (number | null)[];
  excluded_other_generations: number;
  count: number;
  min?: number | null;
  max?: number | null;
  mean?: number | null;
  median?: number | null;
  p10?: number | null;
  histogram?: QualityDistributionBin[] | null;
  bin_width: number;
  suppressed: string[];
  breaches_at_current_threshold?: number | null;
  scale_max: number;
}

export interface QualityThresholdBreach {
  transmission_id: string;
  kind: string;
  quality_score: number;
  quality_score_version?: number | null;
  created_at?: string | null;
  /** In-app link to the run or batch item, so a count can be opened rather than trusted. */
  href: string;
}

export interface QualityThresholdPreview {
  job_id: string;
  proposed_min_quality_score: number;
  current_min_quality_score?: number | null;
  quality_score_version?: number | null;
  /** The denominator. A breach count without it is unreadable — the defect T10.28 fixed. */
  evaluated: number;
  would_breach: number;
  would_pass: number;
  breach_rate?: number | null;
  excluded_other_generations: number;
  breaches: QualityThresholdBreach[];
  breaches_truncated: boolean;
  breaches_at_current_threshold?: number | null;
  breach_delta?: number | null;
  notes: string[];
}

/**
 * One leg's provider-account reference, judged by the backend (U28).
 *
 * `status` mirrors the `LEG_ACCOUNT_*` constants in `backend/services/job_leg_health.py`:
 * `not_applicable` (this leg reads no account), `not_evaluated` (the caller did not load the
 * accounts — UNKNOWN, not healthy), `ok`, `job_columns` (no account named and none required;
 * the job's own stored credentials are used, which still works), `unset`, `missing`,
 * `wrong_kind`, `disabled`.
 *
 * `needs_attention` is the ONE field a surface should branch on to decide whether to shout.
 * Deriving that from `status` in the UI would put the vocabulary in a second place and let the
 * two drift — which is the duplication class U28 exists to remove.
 */
export interface LegAccountHealth {
  status: string;
  expected_account_kind?: string | null;
  account_id?: string | null;
  account_kind?: string | null;
  account_display_name?: string | null;
  /** Operator-facing sentence naming the repair. Null when nothing is wrong. */
  detail?: string | null;
  needs_attention: boolean;
}

export interface Job {
  id: string;
  name: string;
  enabled: boolean;
  metrics_enabled?: boolean;
  quality_check_enabled?: boolean;
  outbound_method: OutboundMethod;
  inbound_method: InboundMethod;
  // WHICH BRAND each leg is configured against. Provider is its own field per leg (D-3),
  // which is what lets `outbound_method: 'smtp'` mean eFax's SMTP gateway on one job and
  // another brand's on the next WITHOUT migrating the stored method value. A job may
  // legitimately name a different provider per leg (G9.8), so these are two fields and
  // never collapsed into one.
  //
  // Absent is rendered as "not recorded", never as eFax — see PROVIDER_NOT_RECORDED in
  // utils/methods.ts.
  outbound_provider?: string | null;
  inbound_provider?: string | null;
  /**
   * SFax account whose credentials serve each leg. The API REFUSES a save where a leg's
   * provider is SFax and its account is unset, so the editor must be able to offer these.
   * Separate per leg deliberately: eFax authenticates its inbound download with the
   * OUTBOUND leg's credentials, and that coupling is why eFax SMTP-send plus
   * webhook-receive cannot complete. SFax resolves per leg and must not inherit it.
   */
  sfax_outbound_account_id?: string | null;
  sfax_inbound_account_id?: string | null;
  /**
   * THE account reference per leg, for every provider and every account-backed transport —
   * the pair the send path actually reads (`backend/services/leg_credentials.py` reads these
   * FIRST and falls back to the `sfax_*` pair above). The `sfax_*` pair is the superseded
   * SFax-only mirror; this editor reads and writes THESE and only echoes a stored `sfax_*`
   * value back so a migrated job is not silently un-mirrored.
   *
   * NULL means no account is named for that leg — which for a leg whose descriptor declares
   * `account_kind: null` (eFax's per-job webhook) is the correct and permanent state.
   */
  outbound_account_id?: string | null;
  inbound_account_id?: string | null;
  /**
   * THE MAILBOX THIS JOB'S OUTBOUND LEG COLLECTS ITS SEND CONFIRMATION FROM (W3) — a
   * `mail`-kind provider account, and OPTIONAL.
   *
   * ADDITIONAL, NEVER A REPLACEMENT for `outbound_account_id`: a leg may name a transport
   * account *and* a notification mailbox, and naming one does not displace a webhook or a
   * status poll — when both a mail notification and a callback arrive, both times are
   * recorded, because the delta between them is the product.
   *
   * NULL DOES NOT MEAN "STOP LOOKING", and that is the backward-compatibility requirement
   * rather than a nicety. An existing eFax SMTP job collects its send notifications from the
   * INBOUND mailbox today, so an unset value means *"a confirmation may still arrive in the
   * inbound box"*. Rendering it as an absence is what would make one provider's working
   * configuration read as broken — see `utils/legMailbox.ts`, which phrases all four
   * permutations of (outbound mailbox, inbound mailbox).
   */
  outbound_mail_account_id?: string | null;
  /**
   * WHETHER EACH LEG CAN STILL FIND THE ACCOUNT IT NAMES (U28) — computed server-side.
   *
   * The repair signal the mail-account consolidation needs. Repointing a leg was already
   * possible below; what was missing is any way to know WHICH jobs need it, because a dangling
   * reference showed up only as a `<uuid> — not in the current list` option inside a dropdown.
   *
   * The verdict is NOT derived in the browser, deliberately: it needs the registry's declared
   * `account_kind` for the leg AND the actual kind of the referenced row, which is one join the
   * frontend should not reassemble — and a second copy of that rule is how two surfaces come to
   * disagree about whether a job is broken.
   *
   * `status: 'not_evaluated'` means the response did not load the account rows (every write
   * path). Treat it as UNKNOWN and render nothing — never as healthy.
   */
  outbound_account_health?: LegAccountHealth | null;
  inbound_account_health?: LegAccountHealth | null;
  /**
   * WHICH API FQDN EACH LEG DIALS, AND WHICH RECORD SUPPLIED IT (W1-F). READ-ONLY.
   *
   * `api_base_url` below is the eFax-only JOB column and stays exactly as it is; these two say
   * what is actually in use. Computed server-side because the answer follows `resolve_leg`'s
   * precedence — a named provider account overrides the job column — and a second copy of that
   * rule in the browser is how two surfaces come to disagree about what a job dials.
   *
   * `status: 'not_evaluated'` means the response did not load the account rows (every write
   * path). Treat it as UNKNOWN and never as the job column. See `utils/apiEndpoint.ts`.
   */
  outbound_api_endpoint?: LegApiEndpoint | null;
  inbound_api_endpoint?: LegApiEndpoint | null;
  /**
   * Per-job quality gate threshold. NULL means the quality dimension is inactive for this
   * job and no quality verdict is produced — it is NOT "zero", which would mean "a score
   * must exist but any value passes". The API accepts it; the editor has no control yet
   * (it arrives with the score re-scaling, because a bare threshold over a saturated
   * score invites an SLA that cannot discriminate).
   */
  min_quality_score?: number | null;
  destination_did: string;
  cover_page_text?: string;
  from_did?: string;
  has_api_credentials: boolean;
  api_base_url?: string;
  efax_smtp_send_domain?: string;
  // Input-only credential fields (write; never returned by the API)
  api_app_id?: string;
  api_key?: string;
  api_user_id?: string;
  smtp_username?: string;
  smtp_password?: string;
  imap_username?: string;
  imap_password?: string;
  smtp_host?: string;
  smtp_port?: number;
  smtp_tls_validate?: boolean;
  from_email?: string;
  has_smtp_credentials: boolean;
  webhook_id?: string;
  webhook_url?: string;
  has_webhook_hmac_secret?: boolean;
  webhook_hmac_secret?: string;
  imap_host?: string;
  imap_port?: number;
  imap_tls_validate?: boolean;
  imap_retention_days: number;
  has_imap_credentials: boolean;
  interval_minutes?: number;
  cron_expression?: string;
  // Per-leg thresholds are the ONLY SLA controls. The former job-wide `sla_minutes`
  // fallback is retired: a leg left null is NOT EVALUATED, and there is no global
  // default standing behind it. A run passes only when EVERY configured leg passes,
  // so a leg breach fails the run even when the total elapsed time would have fitted
  // inside the sum of the legs.
  outbound_sla_minutes?: number | null;
  inbound_sla_minutes?: number | null;
  max_in_flight: number;
  document_original_name?: string;
  document_page_count?: number;
  document_size_bytes?: number;
  created_at: string;
  updated_at: string;
  /**
   * When this job was ARCHIVED, or null/absent while it is live.
   *
   * The default job listing excludes archived jobs, so this is only populated for the
   * archived listing — where it is the whole point: the archive has to be a state an
   * operator can see, because a delete here preserves the run history and history nobody
   * can navigate to is not usefully different from history that was destroyed.
   */
  deleted_at?: string | null;
}

/**
 * What a delete of one job would keep and stop. Returned by GET /api/jobs/{id}/deletion-preview.
 *
 * Every field is here so the confirmation can NAME the consequences rather than gesture at
 * them. `run_count` is the one that matters: a job's runs are measurements, the delete is a
 * configuration action, and the dialog's job is to state that those are separable — with the
 * number, so the operator can see how much evidence is being kept.
 */
export interface JobDeletionPreview {
  job_id: string;
  job_name: string;
  enabled: boolean;
  /** Runs recorded against this job. Preserved by the delete, every one of them. */
  run_count: number;
  first_run_at?: string | null;
  last_run_at?: string | null;
  /** Runs not yet in a terminal status. These are NOT cancelled — they complete. */
  runs_in_flight: number;
  history_action: string;
  history_url: string;
  scheduling_action: string;
  in_flight_action: string;
  webhook_url?: string | null;
  /** What happens to a provider-registered URL, or null when the job has none. */
  webhook_action?: string | null;
  leg_accounts: { leg: string; account_id: string }[];
  accounts_action: string;
  reversible: boolean;
  reversal: string;
}

export interface Run {
  id: string;
  job_id: string;
  run_token: string;
  status: string;
  sla_breached: boolean;
  outbound_sla_breached?: boolean | null;
  inbound_sla_breached?: boolean | null;
  outbound_sla_minutes_effective?: number | null;
  inbound_sla_minutes_effective?: number | null;
  outbound_sla_seconds?: number | null;
  inbound_sla_seconds?: number | null;
  sla_basis?: string;
  /**
   * Submit-to-received DURATION only. There is no combined THRESHOLD any more —
   * thresholds are per leg — so this may be displayed as a measurement but must
   * never be rendered as a pass/fail verdict of its own.
   */
  combined_sla_seconds?: number | null;
  efax_fax_id?: string;
  // The job's send method, needed to tell an SMTP run from a not-yet-sent API run —
  // both have a null efax_fax_id.
  outbound_method?: string;
  outbound_status?: string;
  t_scheduled: string;
  t_submitted?: string;
  t_fax_sent?: string;
  t_received?: string;
  t_quality_checked?: string;
  e2e_duration_seconds?: number;
  quality_result?: string;
  quality_detail?: string;

  /**
   * THE QUALITY SLA AXIS. Declared here because the backend has been sending all of it on
   * every list row and NONE of it was declared, so the fields arrived over the wire and were
   * invisible to the components: the run-history quality column could only reach
   * `quality_result` — the OCR readability enum — and coloured that as if it were the SLA.
   *
   * `quality_score` is the measurement (0-100). For a kind='batch' row it is the aggregate
   * MEAN over the batch's items, not a measurement of one fax.
   *
   * `quality_sla_breached` is the STORED verdict and the only authority on pass/fail: true =
   * under the minimum, false = met it, null = no verdict was reached. On a batch row it is the
   * batch's own quality verdict, derived by the statistics engine from its items' stored votes.
   *
   * `quality_verdict` is pass | fail | undecided | not_configured, derived by the backend from
   * the column above. `not_configured` is deliberately NOT `undecided` — a question never
   * asked must not read as one still open.
   *
   * `min_quality_score_effective` is what the job requires NOW, and is read ONLY to tell "this
   * axis is under no SLA" from "not measured". IT MUST NEVER BE COMPARED TO `quality_score` to
   * derive a verdict: the stored column records the threshold in force AT MEASUREMENT TIME, and
   * recomputing would silently re-judge historical rows against a threshold they were never
   * measured against. NULL on a batch row — a per-fax threshold does not apply to an aggregate,
   * which is why a batch's colour comes from its verdict and not from this being absent.
   *
   * `quality_sla_denominator` is how many items a batch's aggregate verdict was reached over.
   * NULL for a fax: its verdict is a measurement of itself, and "1 of 1" would dress a single
   * reading up as a rate.
   *
   * The rule that consumes these is `utils/qualityCellState.ts`; nothing else should re-derive
   * a quality verdict from them.
   */
  quality_score?: number | null;
  quality_sla_breached?: boolean | null;
  quality_verdict?: string | null;
  quality_sla_reason?: string | null;
  min_quality_score_effective?: number | null;
  quality_sla_denominator?: number | null;
  created_at: string;
  // Populated only by listAllRuns (GET /api/runs) so the global page can show
  // which job each run belongs to.
  job_name?: string;
  // Unified history discriminator: a batch appears as one row alongside runs.
  kind?: 'run' | 'batch';
  batch_size?: number | null;
  /**
   * THE DID LADDER'S DECISION, ON THE LIST ROW (DID-AUTHORITY-LADDER).
   *
   * Carried on the LIST and not only on detail because the operator's ruling is that an
   * uncertain attribution is marked where a reader SCANNING rows will see it. The detail blob
   * is deliberately not here — the marking rule reads only these three, so the listing and run
   * detail cannot disagree about which rows are marked. NULL on a batch aggregate row and on
   * every row measured before the columns existed; `describeDidAttribution` renders that as
   * nothing at all rather than as an uncertainty.
   */
  did_attributed?: string | null;
  did_attribution_rung?: string | null;
  did_attribution_outcome?: string | null;

  /**
   * WHETHER `sending` IS A RESTING STATE OR A FAULT, ON THE LIST ROW.
   *
   * `outbound_settlement` is `poll` | `send_notification` | `none` | `unrecorded`;
   * `outbound_self_settling` is whether the leg advances out of `sending` on its own;
   * `outbound_settlement_marker` is the short label for the status cell, null when it does.
   *
   * DERIVED BY THE BACKEND from the provider registry on every response — there is no column
   * behind them, and the UI must not re-derive them from a provider name. On the LIST for the
   * same reason the DID mark is: a reader SCANNING rows has to see that a row needs no action
   * without opening it. The detail PARAGRAPH is not here; it is the same paragraph for every
   * row sharing a configuration, so it is served on run detail and by the Info endpoint.
   */
  outbound_settlement?: string | null;
  outbound_self_settling?: boolean | null;
  outbound_settlement_marker?: string | null;
}

/** Query for GET /api/runs. Array fields are repeatable (OR within a column). */
export interface RunQuery {
  status?: string[];
  job_id?: string[];
  quality_result?: string[];
  outbound_status?: string[];
  sla?: string[];
  /** Per-leg SLA verdicts: pass | breach | no_verdict. Independent of `sla`. */
  outbound_sla?: string[];
  inbound_sla?: string[];
  /** Glob on the run token: * and ? wildcards, case-insensitive. */
  token?: string;
  /**
   * Time window, ISO8601 UTC, half-open [from, to) on the run's start clock — the SAME
   * column and rule the dashboard aggregates use. These carry a `DashboardStats`
   * `window_from`/`window_to` pair verbatim so a metric tile and this list cannot
   * describe different populations. Omit both to list every row.
   */
  from?: string;
  to?: string;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
  page?: number;
  page_size?: number;
}

/** Paginated envelope. total/total_pages are what let the pager show counts. */
export interface RunPage {
  items: Run[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface RunFilterOptions {
  statuses: string[];
  jobs: { id: string; name: string }[];
  quality_results: string[];
  outbound_statuses: string[];
  sla: string[];
}

/** An OIDC subject seen at login, with the full decoded claim set. */
export interface IdentityRecord {
  subject: string;
  email?: string | null;
  name?: string | null;
  preferred_username?: string | null;
  claims?: Record<string, unknown> | null;
  first_seen_at?: string | null;
  last_seen_at?: string | null;
  login_count: number;
  is_you: boolean;
}

/** The saved role mapping plus live enforcement state. */
export interface RbacConfig {
  role_claim_path?: string | null;
  admin_values: string[];
  updated_at?: string | null;
  updated_by?: string | null;
  /** What the DB toggle says. */
  enforcement_enabled: boolean;
  /** Whether checks actually deny anything — can differ from the toggle when a
   *  guard refuses to enforce, or ROLE_ENFORCEMENT=off overrides it. */
  enforcement_active: boolean;
  /** The ROLE_ENFORCEMENT deploy override: true = forced on, false = forced off,
   *  null = unset and the DB toggle decides. A non-null value means the toggle on
   *  Settings → Identity cannot act — `enforcementLock` in `identityEnforcement.ts`
   *  turns it into the disabled state and its label. Read this field, never the
   *  wording of a `warnings` entry. */
  enforcement_override?: boolean | null;
  resolvable_admins: number;
  bootstrap_configured: boolean;
  warnings: string[];
}

/** Dry-run result for a candidate role mapping. Nothing is enforced. */
export interface RolePreview {
  claim: string;
  admin_values: string[];
  total_identities: number;
  would_be_admin_count: number;
  you_would_be_admin?: boolean | null;
  entries: Array<{
    subject: string;
    email?: string | null;
    name?: string | null;
    claim_present: boolean;
    matched_values: string[];
    value_count: number;
    would_be_admin: boolean;
    is_you: boolean;
  }>;
  warnings: string[];
}

export interface RunDetail extends Run {
  api_status_history?: Array<{ status: string; timestamp: string }>;
  received_page_count?: number;
  /**
   * Tri-state, resolved by a real storage existence check (not age math):
   * null/undefined = nothing was ever stored, true = downloadable,
   * false = stored once but the object is gone (retention expiry).
   */
  received_document_available?: boolean | null;
  inbound_retention_days?: number | null;
  received_file_size_bytes?: number;
  ocr_text?: string;
  ocr_extracted_token?: string;
  manually_failed_at?: string;
  manually_failed_by?: string;
  manually_failed_note?: string;
  webhook_payload_headers?: Record<string, string>;
  webhook_payload_body?: Record<string, unknown>;
  imap_message_id?: string;
  imap_headers?: Record<string, string>;
  quality_score?: number | null;
  quality_score_detail?: Record<string, any> | null;

  // --- Stage 1 measurement + attribution fields ------------------------------
  // All optional and null until the stage that writes each one lands (pod and
  // message-id fields in Stage 3). No UI reads these yet.

  /**
   * eFax pod attribution, parsed from the `J2MsgID` header
   * (`<region>.<env>.<pod>-<hex>`). Stored per LEG rather than per provider, so a
   * second provider needs no schema change — but the UI labels the outbound pod
   * "CCS pod" and the inbound pod "AFC pod", which is what an eFax operator says.
   */
  outbound_pod_name?: string | null;
  outbound_pod_region?: string | null;
  outbound_pod_env?: string | null;
  /**
   * The raw `J2MsgID` value. NOT `smtp_message_id` — that is the sending relay's own
   * MTA queue handle out of its 250 reply, issued by a different system in a format
   * that varies by MTA. Neither can be derived from the other.
   */
  outbound_msg_id?: string | null;
  inbound_pod_name?: string | null;
  inbound_pod_region?: string | null;
  inbound_pod_env?: string | null;
  inbound_msg_id?: string | null;

  /**
   * Pages actually put on the wire for THIS run. Distinct from the job's
   * `document_page_count`, which is mutable: replacing a job's document would
   * otherwise silently rewrite the sent-page count of every historical run.
   */
  sent_page_count?: number | null;
  /** Transmission duration as eFax reports it, in seconds. May be fractional. */
  fax_duration_seconds?: number | null;

  /**
   * Our clock when we learned the fax had been received. Deliberately separate from
   * `t_received`, which is the best estimate of ACTUAL delivery — the IMAP poller
   * wakes every 30s, so treating our fetch time as delivery would charge polling lag
   * to the SLA. `t_notified - t_received` is our own observation delay.
   */
  t_notified?: string | null;
  /** Completion time eFax REPORTS, as opposed to `t_fax_sent`, which we observed. */
  t_efax_completed?: string | null;
  t_efax_notified?: string | null;

  /**
   * Which scoring formula produced `quality_score`. Stage 6 recalibrates without
   * rescoring history, so an old 92 and a new 92 mean different things.
   */
  quality_score_version?: number | null;

  /** The raw `GET /faxes/{id}/metadata` response, stored verbatim. */
  eapi_metadata_json?: Record<string, unknown> | null;

  // --- provenance: where each timestamp came from (G5, G10) -------------------
  // Every one of these is optional AND nullable: they are null on every row written
  // before provenance was recorded, which is the case the UI must describe honestly
  // rather than fill in with a plausible guess.

  /** Provider slug that served each leg, stored per leg — mixed-provider jobs exist. */
  outbound_provider?: string | null;
  inbound_provider?: string | null;
  /** Transport that carried each leg: 'api' | 'smtp' | 'webhook' | 'imap'. */
  outbound_transport?: string | null;
  inbound_transport?: string | null;
  /** Which of the possible sources actually produced `t_received` on THIS row. */
  t_received_source?: string | null;
  /** Which of the possible sources actually produced `t_fax_sent` on THIS row. */
  t_fax_sent_source?: string | null;
  /**
   * THE DID AUTHORITY LADDER'S DECISION AND ITS EVIDENCE (DID-AUTHORITY-LADDER rule 1).
   *
   * `did_attributed` is the DID the fax was determined to have arrived FOR, as written;
   * `did_attribution_rung` is WHICH source said so; `did_attribution_outcome` is `resolved`,
   * `conflict` or `none`; `did_attribution_detail` is `{by_rung, skipped, conflict_between}` —
   * the audit trail, because a verdict with no evidence cannot be judged. All NULL on a row
   * measured before the columns existed, which renders as NOT RECORDED and never as a case.
   */
  did_attributed?: string | null;
  did_attribution_rung?: string | null;
  did_attribution_outcome?: string | null;
  did_attribution_detail?: Record<string, unknown> | null;
  /**
   * THE FULL SENTENCE behind `outbound_settlement`, served where there is room to read it.
   *
   * A marker without an explanation is the half-fix: "no completion feedback" says the row is
   * different and leaves a reader to guess whether that is bad. This says plainly that no action
   * is required and names what WILL close the leg — the inbound leg arriving, or the inbound SLA
   * elapsing. Same voice as the Info panel's notes.
   */
  outbound_settlement_detail?: string | null;
  /**
   * When OUR outbound request started. `t_submitted - t_outbound_request_started` is our
   * own POST latency, an internal phase that never enters an SLA verdict. NULL IS NOT
   * ZERO LATENCY — it means the start was never recorded.
   */
  t_outbound_request_started?: string | null;
  /** When the provider's SMTP confirmation message arrived. */
  smtp_notification_received_at?: string | null;
  /**
   * Resolved provenance per timestamp column, straight from the backend registry, so the
   * description a page shows is a fact about this row rather than a per-transport rule.
   * Absent for a historical row, which the UI labels as an inference.
   */
  timestamp_provenance?: Record<string, {
    code?: string | null;
    rung?: 0 | 1 | 2 | 3 | null;
    event?: string | null;
    field?: string | null;
    transport?: string | null;
    caveat?: string | null;
  }> | null;
}

/**
 * `GET /api/reports/dashboard`.
 *
 * The four keys at the top ship in every backend version and the page may rely on
 * them. EVERYTHING BELOW `avg_e2e_seconds` IS DECLARED OPTIONAL ON PURPOSE, including
 * the two per-leg breach counts the current backend already returns: this interface is
 * an unchecked assertion over a JSON body, so a field that is only *usually* there must
 * be modelled as absent-able or the render path will read `undefined` as a number and
 * print "NaN%" / "undefined" the first time the frontend is deployed ahead of the
 * backend. Dashboard.tsx funnels each of these through a formatter that renders an
 * em dash when the key is missing. Do not "tidy" the `?` off one of them.
 */
export interface DashboardStats {
  period_hours: number;
  total_runs: number;
  successful_runs: number;
  success_rate_percent: number;
  avg_e2e_seconds?: number;

  /** Resolved window the numbers describe (ISO8601), whether asked for by hours or from/to. */
  window_from?: string;
  window_to?: string;

  /** Terminal-only denominators. `total_runs` still counts non-terminal work. */
  terminal_runs?: number;
  failed_runs?: number;
  failure_rate_percent?: number;

  /** Item 3 — page counters. `pages_counted_since` is the earliest row carrying a
   *  non-NULL page count; there is no backfill, so the all-time tile MUST print it. */
  pages_sent_window?: number;
  pages_sent_all_time?: number;
  pages_counted_since?: string | null;

  /**
   * Received-leg page counters, plus the two denominators that say how much of the
   * window the SENT counter actually covers.
   *
   * Why both legs: `sent_page_count` is only written for a run whose job document could
   * be paginated, so the sent tile read 2 across a window holding 8 transmissions and
   * looked like a complete total. Counting the received leg as well makes the two
   * numbers checkable against each other — their disagreement IS the page-loss signal,
   * and neither number alone can show it.
   *
   * `pages_received_*` are nullable as well as optional: the backend returns null, not
   * 0, when nothing in the window carried a page count, because "no pages counted" and
   * "zero pages" are different facts and only one of them is a measurement.
   *
   * Optional for the same reason as every other round-17 key — this interface is an
   * unchecked assertion over a JSON body, and the frontend is deployed ahead of the
   * backend as a matter of routine. Dashboard.tsx funnels all four through the same
   * `fmtInt` em-dash formatter, so an older backend renders "—" rather than "NaN".
   */
  pages_received_window?: number | null;
  /**
   * THE PAIRED POPULATION — the only page figures that may be compared with each other.
   *
   * `pages_sent_window` and `pages_received_window` are each summed over every row that has
   * that one counter, so they describe DIFFERENT populations and their difference is a
   * coverage artefact rather than a measurement. These three are summed over rows carrying
   * BOTH counters, so subtracting them means something. `paired_page_rows_window` is the
   * denominator: 12 pages over 4 paired rows and 12 over 400 are different facts.
   */
  pages_sent_paired_window?: number | null;
  pages_received_paired_window?: number | null;
  paired_page_rows_window?: number | null;
  /**
   * Paired rows that received FEWER pages than they sent, and the total pages missing.
   *
   * Counted per row rather than netted, because a run that lost 2 pages and another that
   * arrived with 2 extra are two events and net to zero — which is how three genuinely
   * dropped pages sat in the live data with nothing reporting them.
   */
  runs_losing_pages_window?: number | null;
  pages_lost_window?: number | null;
  pages_received_all_time?: number | null;
  /** Runs in the window that contributed a non-NULL `sent_page_count`. */
  runs_with_sent_pages_window?: number;
  /** Runs in the window that actually transmitted — the coverage denominator. */
  runs_transmitted_window?: number;

  /** Item 13 — sum(fax_duration_seconds) / sum(sent_page_count) over rows with both. */
  avg_seconds_per_page?: number | null;
  total_fax_duration_seconds?: number;

  active_sla_breaches: number;
  /** Per-leg breach counts. Returned by the backend today; the type used to omit them. */
  active_outbound_sla_breaches?: number;
  active_inbound_sla_breaches?: number;

  /**
   * Breaches that HAPPENED IN THE WINDOW, whatever the run's status now — the same
   * population `/api/runs?sla=breach&from=&to=` returns, which is what makes the SLA
   * tile's drill-through agree with its own number. Distinct from `active_*`, which
   * counts what is overdue right this second and is deliberately period-less.
   * Optional, like every round-17 key: an older backend omits it and the tile falls
   * back to the active count rather than printing a zero it cannot justify.
   */
  sla_breaches_window?: number;
  outbound_sla_breaches_window?: number;
  inbound_sla_breaches_window?: number;

  in_flight_count: number;
  active_jobs: number;
}

/**
 * Window selector for the dashboard. `from`/`to` win over `hours` when BOTH are set,
 * mirroring the backend precedence — a half-filled absolute range falls back to hours
 * rather than sending a one-sided window the backend would have to guess at.
 */
export interface DashboardWindow {
  hours?: number;
  /** ISO8601 (UTC), e.g. from `new Date(local).toISOString()`. */
  from?: string;
  to?: string;
}

export interface JobStats {
  job_id: string;
  period_hours: number;
  sample_count: number;
  p50_seconds?: number;
  p90_seconds?: number;
  p99_seconds?: number;
  min_seconds?: number;
  max_seconds?: number;
  success_rate_percent?: number;
}

/*
 * `GET /api/reports/job/{id}/timeseries` IS DELIBERATELY NOT WRAPPED HERE (W4-4B-3).
 *
 * There was a `jobTimeseries` method and a `TimeseriesPoint` interface, plumbed and called from
 * NOWHERE. Both are gone. An exported client method with no caller reads to the next developer
 * as a supported capability, and this codebase keeps producing that shape — a complete, tested
 * surface that nothing invokes.
 *
 * It was removed rather than wired because the Reports charts cannot use it. It publishes a
 * COMBINED `e2e_seconds` and the `quality_result` readability enum; the per-leg bands need
 * `t_fax_sent` to split the legs at all, and the quality trend needs the numeric
 * `quality_score`. Neither is in that payload. It is also one request per job, where the charts
 * cover every job from one windowed `GET /api/runs`.
 *
 * THE BACKEND ENDPOINT IS UNTOUCHED and still serves; it has its own coverage in
 * tests/test_reports_zero_is_a_measurement.py. If a caller ever wants it, re-add the method AND
 * its call site together — tests/test_reports_charts_gap_and_grouping.py fails on a declaration
 * without a caller.
 */

/**
 * How long a received account list may be used for a FIRST PAINT before it is treated as absent.
 *
 * Five minutes: long enough to cover a session of editing several jobs, short enough that an
 * account created in Settings cannot be missing from a snapshot the operator still believes in.
 * A snapshot is never the only read — see `peekProviderAccounts` — so this bounds staleness on
 * screen for one frame, not staleness of the answer.
 */
const PROVIDER_ACCOUNTS_SNAPSHOT_TTL_MS = 5 * 60 * 1000

let providerAccountsSnapshot: { at: number; value: ProviderAccountSummary[] } | null = null

/**
 * The one request in flight, shared by every concurrent caller.
 *
 * This reads `/settings/provider-accounts/summary`, NOT the full list. The full one decrypts
 * every credential on every row, and credentials are AES-256-GCM under a PBKDF2-SHA256 key with
 * 600 000 iterations and a per-value salt — measured at ~247 ms for one `efax_api` row, so a
 * 40-account page spent about 9.9 SECONDS of server CPU deriving keys this selector never reads.
 * That, more than the 2N+1 round trips fixed alongside it, was the 5-9 second dropdown.
 *
 * The summary serves exactly what the selector uses (`id`, `account_kind`, `kind_label`,
 * `display_name`, `enabled`, `dids`, `derived_dids`) in two SQL statements and zero key
 * derivations. `tests/test_account_summary_does_not_decrypt.py` counts the derivations and
 * requires zero, so a field that reintroduces one fails rather than quietly costing seconds.
 *
 * Sharing the in-flight promise stays worthwhile: only ONE caller exists today (the job editor),
 * and this makes a second one free rather than doubling the cost.
 */
let providerAccountsInFlight: Promise<ProviderAccountSummary[]> | null = null

function fetchProviderAccounts(): Promise<ProviderAccountSummary[]> {
  if (providerAccountsInFlight) return providerAccountsInFlight
  const inFlight = request<ProviderAccountSummary[]>(
    `${BASE_URL}/settings/provider-accounts/summary`,
  )
    .then(rows => {
      const value = rows ?? []
      providerAccountsSnapshot = { at: Date.now(), value }
      return value
    })
    .finally(() => {
      providerAccountsInFlight = null
    })
  providerAccountsInFlight = inFlight
  // A REJECTION LEAVES THE SNAPSHOT ALONE, deliberately. A failed refresh must not erase the
  // list the operator can currently see: that would turn one display outage into an empty
  // selector, which is the exact misreading `providerAccountsError` was added to prevent.
  return inFlight
}

export const api = {
  // Jobs
  listJobs: (enabled?: boolean) =>
    request<Job[]>(`${BASE_URL}/jobs${enabled !== undefined ? `?enabled=${enabled}` : ''}`),
  getJob: (id: string) => request<Job>(`${BASE_URL}/jobs/${id}`),

  /**
   * The shape of this job's quality scores, for the threshold control to sit beside (T10.31).
   *
   * Deliberately a plain GET with no session requirement beyond the app's own: it aggregates
   * scores a viewer can already read one run at a time. A failure is NOT fatal to the editor —
   * the threshold field still saves — because a statistics outage must not make a job
   * un-editable, the same posture `listProviders` takes.
   *
   * Figures the sample cannot support come back `null` with a sentence in `suppressed`. Render
   * the sentence; do not substitute a number. A histogram over four faxes is what this
   * endpoint exists to refuse.
   */
  getQualityDistribution: (jobId: string) =>
    request<QualityDistribution>(`${BASE_URL}/jobs/${jobId}/quality-distribution`),

  /**
   * How many of this job's past transmissions would breach at a proposed threshold (T10.32).
   *
   * POST because the spec names it so, which the auth middleware turns into an admin-only
   * call — acceptable here because the caller is the job editor, which is already admin-only.
   */
  previewJobQualityThreshold: (jobId: string, minQualityScore: number) =>
    request<QualityThresholdPreview>(`${BASE_URL}/jobs/${jobId}/quality-threshold-preview`, {
      method: 'POST',
      body: JSON.stringify({ min_quality_score: minQualityScore }),
    }),

  // The provider registry the job editor renders its per-leg forms from. Read-only, needs
  // no session on the backend, and cannot 500 on the database — so the editor treats a
  // failure as "descriptor unavailable" and falls back rather than blocking the form.
  // T4.20 — adopting the backend's label tables as a SIDE EFFECT of the fetch every page
  // already makes, rather than requiring each caller to remember. `methods.ts` keeps its
  // literals as a build-time fallback, so a failed fetch here degrades to today's labels
  // instead of blanking the only thing that tells an operator which vendor a row belongs to.
  listProviders: () =>
    request<{ providers: ProviderDescriptor[] }>(`${BASE_URL}/providers`).then(r => {
      applyProviderLabelTables(r.providers ?? []);
      return r;
    }),
  // SFax account records, for the job editor's per-leg account selectors. SFax resolves
  // credentials per ACCOUNT rather than per job, so a job on SFax must name one — the API
  // refuses the save otherwise, which is why the editor has to be able to offer them.
  listSfaxAccounts: () => request<SfaxAccountSummary[]>(`${BASE_URL}/settings/sfax-accounts`),
  // The SAME table through the general surface, which is where `dids` lives. The job editor
  // needs them to offer a destination DID instead of a free-text number (T5.41); the pinned
  // alias above does not carry them, so this is an addition rather than a replacement.
  listProviderAccounts: () => fetchProviderAccounts(),
  /**
   * The last list this session actually received, or `null`.
   *
   * NOT a substitute for the fetch — every caller of this must still call
   * `listProviderAccounts()` — so nothing is ever shown from cache without being revalidated
   * against the server in the same breath. What it buys is the FIRST PAINT: the second and every
   * later visit to a job editor renders a populated selector immediately instead of spending
   * another 5-9 seconds looking broken, which is the part of the reported symptom that is
   * fixable from the frontend at all (the request cost itself is server-side — see
   * `docs/UI-ACCOUNT-DROPDOWN-LATENCY.md`).
   *
   * TTL'd rather than kept for the session, because an account created in Settings must not be
   * absent from a snapshot old enough that the operator has forgotten taking it.
   */
  peekProviderAccounts: (): ProviderAccountSummary[] | null => {
    if (!providerAccountsSnapshot) return null
    if (Date.now() - providerAccountsSnapshot.at > PROVIDER_ACCOUNTS_SNAPSHOT_TTL_MS) return null
    return providerAccountsSnapshot.value
  },
  createJob: (data: Partial<Job>) => request<Job>(`${BASE_URL}/jobs`, { method: 'POST', body: JSON.stringify(data) }),
  updateJob: (id: string, data: Partial<Job>) => request<Job>(`${BASE_URL}/jobs/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteJob: (id: string) => request<void>(`${BASE_URL}/jobs/${id}`, { method: 'DELETE' }),
  /**
   * What a delete of this job would keep and stop — a READ, safe to call when a dialog opens.
   *
   * The delete is a SOFT delete: every run survives. But an operator cannot be asked to trust
   * that from a dialog that says "Are you sure?", so the confirmation quotes these numbers
   * instead. Fetched rather than derived client-side because the run count is not on the job
   * response and a count assembled from a paged run listing would be wrong by however many
   * pages the caller did not fetch.
   */
  deletionPreview: (id: string) => request<JobDeletionPreview>(`${BASE_URL}/jobs/${id}/deletion-preview`),
  /**
   * Duplicate a job's configuration as a new, DISABLED job.
   *
   * Server-side because the credentials it must copy are never in a response body — a
   * client-side get-then-create would produce a job that looks complete and has none. The
   * clone gets a fresh webhook id; the source's registered URL is left alone.
   */
  cloneJob: (id: string) => request<Job>(`${BASE_URL}/jobs/${id}/clone`, { method: 'POST' }),
  /** Archived (soft-deleted) jobs. Their run history is intact and reachable from /runs. */
  listArchivedJobs: () => request<Job[]>(`${BASE_URL}/jobs?deleted_only=true`),
  /**
   * Un-archive a job, keeping its ORIGINAL webhook URL.
   *
   * Not a clone. A clone mints a fresh webhook id, which would retire a URL already
   * registered in the provider's portal — unrecoverable from inside this app. Restore returns
   * the job behind the same path, so a provider still posting to it starts resolving again.
   * The job comes back DISABLED, so nothing transmits until it is explicitly turned on.
   */
  restoreJob: (id: string) => request<Job>(`${BASE_URL}/jobs/${id}/restore`, { method: 'POST' }),
  testConnection: (id: string) => request<{ success: boolean; results: Record<string, { status: string; message: string }> }>(`${BASE_URL}/jobs/${id}/test-connection`, { method: 'POST' }),
  testNow: (id: string) => request<{ status: string; job_id: string }>(`${BASE_URL}/jobs/${id}/test-now`, { method: 'POST' }),
  uploadDocument: (id: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return fetch(`${BASE_URL}/jobs/${id}/upload-document`, { method: 'POST', body: form }).then(r => r.json());
  },

  // Runs
  listRuns: (jobId: string, params?: { status?: string; page?: number; page_size?: number }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.page_size) qs.set('page_size', String(params.page_size));
    return request<Run[]>(`${BASE_URL}/jobs/${jobId}/runs?${qs}`);
  },
  listAllRuns: (params?: RunQuery) => {
    const qs = new URLSearchParams();
    // Repeatable params: the backend OR-s within a column and AND-s across
    // columns, so append each selected value rather than joining them.
    params?.status?.forEach(v => qs.append('status', v));
    params?.job_id?.forEach(v => qs.append('job_id', v));
    params?.quality_result?.forEach(v => qs.append('quality_result', v));
    params?.outbound_status?.forEach(v => qs.append('outbound_status', v));
    params?.sla?.forEach(v => qs.append('sla', v));
    params?.outbound_sla?.forEach(v => qs.append('outbound_sla', v));
    params?.inbound_sla?.forEach(v => qs.append('inbound_sla', v));
    if (params?.token) qs.set('token', params.token);
    // The window is sent ONLY when both ends are known, mirroring dashboardStats: a
    // one-sided window would list a different period from the metrics above it, which is
    // the exact disagreement this pair exists to prevent.
    if (params?.from && params?.to) {
      qs.set('from', params.from);
      qs.set('to', params.to);
    }
    if (params?.sort_by) qs.set('sort_by', params.sort_by);
    if (params?.sort_dir) qs.set('sort_dir', params.sort_dir);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.page_size) qs.set('page_size', String(params.page_size));
    return request<RunPage>(`${BASE_URL}/runs?${qs}`);
  },
  getRunFilterOptions: () => request<RunFilterOptions>(`${BASE_URL}/runs/filter-options`),

  // --- Log viewer ---
  getLogSources: () => request<LogSourceInfo>(`${BASE_URL}/settings/logs/sources`),
  getAppLogs: (p: { hours: number; search?: string; level?: string; limit?: number }) => {
    const qs = new URLSearchParams()
    qs.append('hours', String(p.hours))
    if (p.search) qs.append('search', p.search)
    if (p.level) qs.append('level', p.level)
    if (p.limit) qs.append('limit', String(p.limit))
    return request<AppLogResponse>(`${BASE_URL}/settings/logs/app?${qs}`)
  },
  getAlbLogs: (p: { hours: number; search?: string; limit?: number }) => {
    const qs = new URLSearchParams()
    qs.append('hours', String(p.hours))
    if (p.search) qs.append('search', p.search)
    if (p.limit) qs.append('limit', String(p.limit))
    return request<AlbLogResponse>(`${BASE_URL}/settings/logs/alb?${qs}`)
  },
  getEcsEvents: () => request<EcsEventsResponse>(`${BASE_URL}/settings/logs/ecs`),

  // --- Audit log ---
  getAuditEntries: (params: Record<string, string | string[] | number | undefined>) => {
    const qs = new URLSearchParams()
    Object.entries(params).forEach(([k, v]) => {
      if (v === undefined || v === '') return
      // Repeatable params: ?actor=a&actor=b, matching the runs table semantics
      // (AND across fields, OR within a field).
      if (Array.isArray(v)) v.forEach(item => qs.append(k, String(item)))
      else qs.append(k, String(v))
    })
    return request<AuditPage>(`${BASE_URL}/settings/audit?${qs.toString()}`)
  },
  getAuditFilterOptions: () =>
    request<AuditFilterOptions>(`${BASE_URL}/settings/audit/filter-options`),
  getAuditRetention: () =>
    request<AuditRetentionInfo>(`${BASE_URL}/settings/audit/retention`),
  /** Raw Prometheus exposition text, behind the OIDC session rather than Basic Auth. */
  getMetricsSnapshot: () =>
    fetch(`${BASE_URL}/settings/metrics-snapshot`, { credentials: 'same-origin' })
      .then(r => {
        if (!r.ok) throw new Error(`Snapshot failed (${r.status})`);
        return r.text();
      }),
  listIdentities: () => request<IdentityRecord[]>(`${BASE_URL}/identity/claims`),
  getClaimPaths: () => request<string[]>(`${BASE_URL}/identity/claim-paths`),
  getClaimValues: (path: string) =>
    request<string[]>(`${BASE_URL}/identity/claim-values?path=${encodeURIComponent(path)}`),
  getRbacConfig: () => request<RbacConfig>(`${BASE_URL}/identity/rbac-config`),
  putRbacConfig: (body: { role_claim_path: string; admin_values: string[]; enforcement_enabled?: boolean }) =>
    request<RbacConfig>(`${BASE_URL}/identity/rbac-config`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  previewRole: (body: { claim: string; admin_values: string[] }) =>
    request<RolePreview>(`${BASE_URL}/identity/role-preview`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getRunDetail: (id: string) => request<RunDetail>(`${BASE_URL}/runs/${id}`),
  resolveRun: (id: string, note: string) => request<{ status: string }>(`${BASE_URL}/runs/${id}/resolve`, { method: 'POST', body: JSON.stringify({ note }) }),
  deleteRun: (id: string) => request<void>(`${BASE_URL}/runs/${id}`, { method: 'DELETE' }),
  // SMTP counterpart to checkRunStatus. A GET, because unlike check-status there
  // is nothing to refresh from eFax — SMTP has no metadata endpoint, so everything
  // here was captured locally at send time and from the Send Notification email.
  getSmtpInfo: (id: string) => request<SmtpInfo>(`${BASE_URL}/runs/${id}/smtp-info`),
  // The same view, for EITHER row type. `runs/{id}/smtp-info` resolved runs only, so Info on
  // a batch item 404'd; this one resolves both and reports which table answered. Every Info
  // click goes through here first, because it is the only place that knows the row's
  // PROVIDER — and the provider, not the presence of a handle, is what decides whether eFax's
  // metadata endpoint can be asked about this transmission at all.
  getOutboundInfo: (id: string) =>
    request<OutboundInfo>(`${BASE_URL}/transmissions/${id}/outbound-info`),
  checkRunStatus: (id: string) => request<RunStatusInfo>(`${BASE_URL}/runs/${id}/check-status`, { method: 'POST' }),
  bulkResolve: (runIds: string[], note: string) => request<{ resolved: number; skipped: number }>(`${BASE_URL}/runs/bulk-resolve`, { method: 'POST', body: JSON.stringify({ run_ids: runIds, note }) }),

  // Reports
  // Accepts either a bare hour count (every pre-existing caller) or a DashboardWindow.
  // `from`+`to` are only sent when BOTH are present, so a partially filled absolute
  // range degrades to the relative span instead of sending a one-sided window.
  dashboardStats: (range: number | DashboardWindow = 24) => {
    const qs = new URLSearchParams();
    if (typeof range === 'number') {
      qs.set('hours', String(range));
    } else if (range.from && range.to) {
      qs.set('from', range.from);
      qs.set('to', range.to);
    } else {
      qs.set('hours', String(range.hours ?? 24));
    }
    return request<DashboardStats>(`${BASE_URL}/reports/dashboard?${qs}`);
  },
  // No `jobTimeseries` here on purpose — see the note that follows the `JobStats` interface.
  jobStats: (jobId: string, hours = 24) => request<JobStats>(`${BASE_URL}/reports/job/${jobId}/stats?hours=${hours}`),
  exportCsv: (hours = 24, jobId?: string) => {
    const qs = new URLSearchParams({ hours: String(hours) });
    if (jobId) qs.set('job_id', jobId);
    window.open(`${BASE_URL}/reports/export?${qs}`, '_blank');
  },

  // Document
  downloadDocument: (jobId: string) => window.open(`${BASE_URL}/jobs/${jobId}/document`, '_blank'),

  // Auth
  getUser: () => request<{ authenticated: boolean; user?: { email: string; name: string } }>('/auth/me'),
  // Auth gate probe: GET /auth/me at root (NOT under /api). Returns the raw Response
  // so callers can branch on 200 (authenticated) vs 401 (redirect to login).
  getMe: () => fetch('/auth/me', { credentials: 'same-origin' }),
};

// --- Audit log types ---

export interface AuditEntry {
  id: string
  ts: string
  actor_sub: string | null
  actor_email: string | null
  actor_role: string | null
  action: string
  http_method: string
  http_path: string
  http_status: number | null
  resource_type: string | null
  resource_id: string | null
  /** Ties the entry to the JSON application logs for the same request. */
  request_id: string | null
  source_ip: string | null
  user_agent: string | null
  detail: Record<string, unknown> | null
  /**
   * How the request AUTHENTICATED, which is not the same as who made it (U1).
   *
   * `session` — a logged-in actor (the identity itself is shown instead).
   * `slack_signature_verified` — a Slack HMAC that verified. Authenticated, not anonymous.
   * `slack_signature_failed` — a signature was PRESENT and did NOT verify. Alarm on this one.
   * `unauthenticated` — nothing was presented to check.
   * `not_recorded` — written before the evidence was captured. Historical rows are never
   *   relabelled: marking one verified would assert a verification nobody performed.
   *
   * Derived server-side from `detail._actor_auth`, so it is always present on a response even
   * for rows that predate the field.
   */
  actor_auth: string
}

export interface AuditPage {
  items: AuditEntry[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

export interface AuditFilterOptions {
  actors: string[]
  actions: string[]
  resource_types: string[]
  roles: string[]
}

export interface AuditRetentionInfo {
  retention_days: number
  oldest_entry: string | null
  total_entries: number
}

// --- Log viewer types ---

export interface LogSourceInfo {
  app_log_group: string | null
  alb_log_bucket: string | null
  ecs_cluster: string | null
  ecs_service: string | null
  region: string
  max_lookback_hours: number
  max_events: number
}

export interface AppLogEvent {
  timestamp: number | null
  raw: string
  level: string | null
  logger: string | null
  msg: string
  request_id: string | null
  run_token: string | null
  actor: string | null
  fields: Record<string, unknown>
  structured: boolean
}

export interface AppLogResponse {
  events: AppLogEvent[]
  /** More matched than shown, and the omitted rows are OLDER than the oldest shown. */
  truncated: boolean
  /** The pagination cap was hit before the end of the window, so the NEWEST rows
   *  were never fetched. The opposite problem from `truncated`, with the opposite
   *  remedy, so the two are never merged into one message. */
  page_limit_reached?: boolean
  log_group?: string | null
  error: string | null
}

export interface AlbLogEvent {
  raw: string
  time: string | null
  client_ip: string | null
  method: string | null
  url: string | null
  elb_status_code: string | null
  target_status_code: string | null
  target_processing_time: string | null
}

export interface AlbLogResponse {
  events: AlbLogEvent[]
  truncated: boolean
  bucket?: string | null
  delivery_lag_note?: string | null
  error: string | null
}

export interface EcsEvent {
  timestamp: number | null
  message: string | null
}

export interface EcsDeployment {
  status: string | null
  rollout_state: string | null
  rollout_reason: string | null
  desired: number | null
  running: number | null
  pending: number | null
  task_definition: string | null
  created_at: string | null
}

export interface EcsEventsResponse {
  events: EcsEvent[]
  deployments: EcsDeployment[]
  service_status?: string | null
  desired_count?: number | null
  running_count?: number | null
  error: string | null
}
